"use client";

import { getFDListData } from "@/app/api/service/fixed-deposits";
import Info from "@/app/assets/icons/info";
import Breadcrumb, { type BreadcrumbPath } from "@/app/components/breadcrumb.client";
import Faq from "@/app/components/faq";
import ICMWrapper from "@/app/components/icm-wrapper";
import { FDProduct } from "@/app/interface/fd";
import NewsCard from "@/app/alts/unlisted-shares/components/see-insights";
import { formatIndianCurrency } from "@/app/utils/helper";
import { Badge, BadgeSize, BadgeVariant } from "@/stories/badge/badge";
import { ButtonSize, ButtonVariant, RegularButton } from "@/stories/button/button";
import { useQuery } from "@tanstack/react-query";
import Image from "next/image";
import { useParams } from "next/navigation";
import React, { useEffect, useMemo, useState } from "react";
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import PlayStore from "@/app/assets/icons/play-store";
import useMobile from "@/app/hooks/use-mobile";
import ScrollableBtn from "./scrollable.btn";
import FdpageSkeleton from "@/app/components/loaders/fdpage-skeleton";
import { DirectPaths } from "@/config/routes";

const Page: React.FC = () => {
    const { name } = useParams();
    const { isMobile } = useMobile();

    const { data: fdDataRaw, isFetched } = useQuery({
        queryKey: ["fdListData"],
        queryFn: getFDListData,
        enabled: !!name,
    });

    const fdData: FDProduct = useMemo(() => {
        return (
            fdDataRaw?.data?.filter(
                (fd: FDProduct) => fd.displayName?.split(" ").join("-").toLowerCase() === name,
            )?.[0] || []
        );
    }, [fdDataRaw, name]);

    const [tenure, setTenure] = useState<number>();
    const [isModalOpen, setisModalOpen] = useState<boolean>(false);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [isSeniorCitizen, setisSeniorCitizen] = useState<boolean>(false);
    const [isWomanBenefit, setisWomanBenefit] = useState<boolean>(false);
    const [amtInvested, setamtInvested] = useState<number>();

    useEffect(() => {
        if (fdData && isFetched) {
            setTenure(fdData?.returnsAndTenure[0]?.tenure);
            setamtInvested(
                fdData?.cardAmounts && fdData?.cardAmounts?.every((amt) => amt?.value > 0)
                    ? fdData?.cardAmounts?.filter((amt) => amt?.tagStatus)[0]?.value
                    : fdData?.minAmt,
            );
        }
    }, [fdData, isFetched]);

    useEffect(() => {
        if (isFetched && fdData) {
            getInterest();
            getMaturityAmount();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSeniorCitizen, isWomanBenefit, tenure, isFetched, fdData]);

    const getInterest = () => {
        const result = fdData?.returnsAndTenure.filter((item) => item.tenure === tenure)[0];

        if (isSeniorCitizen && isWomanBenefit) {
            return result?.returns + result?.womenBenefits + result?.seniorCitizenBenefits;
        } else if (isSeniorCitizen) {
            return result?.returns + result?.seniorCitizenBenefits;
        } else if (isWomanBenefit) {
            return result?.returns + result?.womenBenefits;
        }
        return result?.returns;
    };

    const getMaturityAmount = (compoundingPeriodsPerYear = 4) => {
        if ((amtInvested ?? 0) < fdData?.minAmt) {
            return 0;
        }

        const interest = getInterest();
        if (!interest || !tenure || !amtInvested) return 0;

        const principal = amtInvested;
        const annualRate = interest / 100;
        const timeInYears = tenure / 12;

        const maturityAmount =
            principal *
            Math.pow(
                1 + annualRate / compoundingPeriodsPerYear,
                compoundingPeriodsPerYear * timeInYears,
            );

        return parseFloat(String(maturityAmount));
    };

    const onClosePopup = () => setisModalOpen(false);

    return (
        <>
            {!isFetched ? (
                <FdpageSkeleton />
            ) : (
                <>
                    <div className="flex flex-col bg-[#EFF6FF] p-s9 md:hidden">
                        <Image
                            src={fdData?.logoUrl}
                            alt={fdData?.displayName}
                            width={84}
                            height={84}
                            className="mb-3 rounded-lg"
                        />
                        <div className="mb-8 flex flex-col">
                            <h1
                                className="mb-3 truncate text-lg font-semibold leading-[130%] tracking-[-0.24px] md:text-3xl"
                                title={fdData?.displayName}
                            >
                                {fdData?.displayName}
                            </h1>
                            <div className="flex gap-3">
                                {fdData?.productTags?.slice(0, 2).map((highlight, index) => {
                                    return (
                                        <Badge
                                            key={index}
                                            label={highlight.tag}
                                            variant={BadgeVariant.Warning}
                                            size={BadgeSize.Large}
                                            showIcon={false}
                                            showCross={false}
                                            className={`font-medium ${isMobile ? "text-xs" : "text-sm"}`}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                        <div className="flex items-start justify-stretch gap-6">
                            <div className="flex min-w-[139px] flex-col justify-center text-left">
                                <p className="text-md font-regular leading-[160%] text-neutral-500 md:h-6">
                                    Returns upto
                                </p>
                                <p className="text-left text-[25.996px] font-semibold leading-[130%] tracking-[-0.26px] md:h-[34px]">
                                    {Number(
                                        (
                                            Number(fdData?.maxReturns?.returns) +
                                            Number(fdData?.maxReturns?.seniorCitizenBenefits)
                                        ).toFixed(2),
                                    )}
                                    % <span className="text-md font-normal">p.a.</span>
                                </p>
                            </div>
                            <div className="flex flex-col justify-center text-left">
                                <p className="text-md font-regular leading-[160%] text-neutral-500 md:h-6">
                                    Tenure
                                </p>
                                <p className="text-left text-[25px] font-semibold leading-[130%] tracking-[-0.26px] md:h-[34px]">
                                    {fdData?.maxReturns?.tenure} months
                                </p>
                            </div>
                        </div>
                    </div>
                    <ICMWrapper>
                        <Breadcrumb
                            paths={
                                [
                                    { label: "Home", href: process.env.ICM_POST_URL },
                                    { label: "FDs", href: process.env.ICM_FD_POST_URL + DirectPaths.FD_POSTFIX_PATH },
                                    { label: fdData?.displayName || String(name) },
                                ] as BreadcrumbPath[]
                            }
                        />
                        <div className="flex flex-col md:flex-row md:gap-9 md:pb-8 md:pt-s16">
                            <div id="left" className="md:w-[75%]">
                                <div className="hidden rounded-xl bg-[#EFF6FF] md:block">
                                    <div className="md:px-6 md:py-8">
                                        <div className="flex gap-3">
                                            <Image
                                                src={fdData?.logoUrl}
                                                alt={fdData?.displayName}
                                                width={84}
                                                height={84}
                                                className="rounded-lg"
                                            />
                                            <div className="flex min-w-[400px] max-w-[470px] flex-col justify-center gap-3 border-neutral-200 md:mr-9 md:border-r-2 md:border-dashed">
                                                <h1
                                                    className="truncate font-semibold leading-[130%] tracking-[-0.24px] md:text-3xl"
                                                    title={fdData?.displayName}
                                                >
                                                    {fdData?.displayName}
                                                </h1>
                                                <div className="flex gap-3">
                                                    {fdData?.productTags
                                                        ?.slice(0, 2)
                                                        .map((highlight, index) => {
                                                            return (
                                                                <Badge
                                                                    key={index}
                                                                    label={highlight.tag}
                                                                    variant={BadgeVariant.Warning}
                                                                    size={BadgeSize.Large}
                                                                    showIcon={false}
                                                                    showCross={false}
                                                                />
                                                            );
                                                        })}
                                                </div>
                                            </div>
                                            <div className="flex gap-6">
                                                <div className="flex flex-col justify-center">
                                                    <p className="leading-[160% font-regular] text-md text-neutral-500 md:h-6">
                                                        Returns upto
                                                    </p>
                                                    <p className="text-left text-[25px] font-semibold leading-[130%] tracking-[-0.26px] md:h-[34px]">
                                                        {Number(
                                                            (
                                                                Number(
                                                                    fdData?.maxReturns?.returns,
                                                                ) +
                                                                Number(
                                                                    fdData?.maxReturns
                                                                        ?.seniorCitizenBenefits,
                                                                )
                                                            ).toFixed(2),
                                                        )}
                                                        %{" "}
                                                        <span className="text-[20px] font-normal">
                                                            p.a.
                                                        </span>
                                                    </p>
                                                </div>
                                                <div className="flex flex-col justify-center">
                                                    <p className="leading-[160% font-regular] text-md text-neutral-500 md:h-6">
                                                        Tenure
                                                    </p>
                                                    <p className="text-left text-[25px] font-semibold leading-[130%] tracking-[-0.26px] md:h-[34px]">
                                                        {fdData?.maxReturns?.tenure} months
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {fdData?.highlights && (
                                    <div className="py-14 md:py-s17">
                                        <h2 className="mb-6 text-3xl font-semibold tracking-[-0.4px] md:mb-s11 md:text-5xl">
                                            Why to Invest now?
                                        </h2>

                                        {fdData?.highlights?.map((highlight, index) => {
                                            return (
                                                <div
                                                    key={index}
                                                    className={`max-h-auto md:min-h-16 ${index !== fdData?.highlights?.length - 1 ? "mb-6 md:mb-8" : ""}`}
                                                >
                                                    <h5 className="text-md font-semibold leading-[160%] tracking-[-0.18px] md:text-lg">
                                                        {highlight?.web}:
                                                    </h5>
                                                    <p className="whitespace-pre-line text-md font-regular leading-[160%]">
                                                        {highlight?.description}
                                                    </p>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {fdData?.whyInvest && (
                                    <div>
                                        <h2 className="mb-s11 text-3xl font-semibold tracking-[-0.4px] md:text-5xl">
                                            Why {fdData?.displayName}?
                                        </h2>

                                        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                                            {fdData?.whyInvest.map((highlight, index) => {
                                                return (
                                                    <div key={index}>
                                                        <Image
                                                            src={highlight.imageUrl}
                                                            height={72}
                                                            width={72}
                                                            alt={highlight.header}
                                                            className="mb-s8"
                                                        />
                                                        <h5 className="mb-s6 text-md font-semibold leading-[130%] md:text-xl">
                                                            {highlight.header}
                                                        </h5>
                                                        <p className="text-md font-regular leading-[140%] text-neutral-500">
                                                            {highlight.subHeader}
                                                        </p>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {fdData?.blogs?.enable && (
                                    <div className="my-s17">
                                        <NewsCard
                                            newsData={fdData?.blogs?.data}
                                            showViewAll={false}
                                        />
                                    </div>
                                )}

                                {fdData?.product && (
                                    <Faq
                                        bgColor="bg-white"
                                        product="fd"
                                        subProduct={fdData?.product}
                                        compact={true}
                                    />
                                )}
                            </div>

                            <div id="right" className="relative hidden w-[30%] md:block">
                                <div className="sticky top-6">
                                    <div className="rounded-xl border-2 border-neutral-50 px-s9 shadow-md">
                                        <h3 className="border-b-2 border-dashed border-neutral-50 py-s9 text-xl font-semibold leading-[130%]">
                                            Calculate your earnings
                                        </h3>
                                        <div
                                            className={`${
                                                !fdData?.cardAmounts ||
                                                fdData?.cardAmounts?.every(
                                                    (amt) => amt?.value === 0,
                                                )
                                                    ? "pt-s7"
                                                    : "py-s7"
                                            }`}
                                        >
                                            <div className="flex h-8 justify-between">
                                                <p className="text-sm leading-[160%] tracking-[0.14px] text-neutral-500">
                                                    Invested amount
                                                </p>
                                                <input
                                                    type={!isEditing ? "text" : "number"}
                                                    max={fdData?.maxAmt}
                                                    id="amount-input"
                                                    value={
                                                        isEditing
                                                            ? amtInvested || ""
                                                            : amtInvested
                                                              ? formatIndianCurrency(amtInvested)
                                                              : ""
                                                    }
                                                    onFocus={() => setIsEditing(true)}
                                                    onBlur={() => {
                                                        setIsEditing(false);
                                                        if (
                                                            amtInvested === null ||
                                                            amtInvested === undefined
                                                        ) {
                                                            setamtInvested(0);
                                                        }
                                                    }}
                                                    onChange={(e) => {
                                                        const value =
                                                            e.target.value === ""
                                                                ? 0
                                                                : Math.min(
                                                                      Number(e.target.value),
                                                                      fdData?.maxAmt || 0,
                                                                  );
                                                        setamtInvested(value >= 0 ? value : 0);
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (
                                                            e.key === "-" ||
                                                            e.key === "e" ||
                                                            e.key === "E" ||
                                                            e.key === "+"
                                                        ) {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    className="w-28 border-none bg-transparent text-right text-md font-medium leading-[160%] tracking-[0.14px] focus:outline-none"
                                                    placeholder="0"
                                                />
                                            </div>
                                        </div>
                                        {fdData?.cardAmounts &&
                                        fdData?.cardAmounts?.every((amt) => amt?.value > 0) ? (
                                            <div className="flex justify-center gap-3">
                                                {fdData?.cardAmounts
                                                    ?.sort((a, b) => a.value - b.value)
                                                    ?.map((box, index) => (
                                                        <div
                                                            key={index}
                                                            onClick={() =>
                                                                setamtInvested(box.value)
                                                            }
                                                            className="relative flex flex-col items-center"
                                                        >
                                                            {box.tagStatus && (
                                                                <span className="absolute -top-5 rounded-md bg-red-100 px-2 py-1 text-xs font-bold text-black">
                                                                    {box?.tag}
                                                                </span>
                                                            )}

                                                            <div
                                                                className={`cursor-pointer rounded-lg border-2 border-neutral-100 px-3 py-[6px] text-neutral-500 ${Number(amtInvested) === Number(box.value) ? "bg-slate-50" : "bg-white"} hover:bg-slate-50`}
                                                            >
                                                                {formatIndianCurrency(box.value)}
                                                            </div>
                                                        </div>
                                                    ))}
                                            </div>
                                        ) : null}

                                        <div
                                            className={`${
                                                !fdData?.cardAmounts ||
                                                fdData?.cardAmounts?.every(
                                                    (amt) => amt?.value === 0,
                                                )
                                                    ? "pt-s5"
                                                    : "pt-s7"
                                            }`}
                                        >
                                            <div className="flex h-8 justify-between">
                                                <p className="text-sm leading-[160%] tracking-[0.14px] text-neutral-500">
                                                    Interest rate (p.a.)
                                                </p>
                                                <p className="text-md font-medium leading-[160%] tracking-[0.14px]">
                                                    {parseFloat(String(getInterest())).toFixed(2)}%
                                                </p>
                                            </div>
                                        </div>

                                        <div className="mt-s4 flex h-[19px] items-center justify-between text-nowrap">
                                            <div className="flex items-center gap-[6px]">
                                                <input
                                                    type="checkbox"
                                                    id="senior-citizen"
                                                    className="h-4 w-4 cursor-pointer rounded-full text-white accent-brandAccent"
                                                    onChange={(e) =>
                                                        setisSeniorCitizen(e.target.checked)
                                                    }
                                                />
                                                <div className="flex items-center gap-1">
                                                    <label
                                                        htmlFor="senior-citizen"
                                                        className="cursor-pointer text-sm leading-[160%] tracking-[0.14px] text-neutral-500"
                                                    >
                                                        Senior citizen
                                                    </label>
                                                    <div className="group relative">
                                                        <Info
                                                            width="13"
                                                            height="13"
                                                            className="cursor-pointer text-neutral-300"
                                                        />
                                                        <div className="absolute bottom-full left-[-5rem] mb-2 hidden w-36 rounded-md bg-black p-2 text-xs text-white group-hover:block">
                                                            Age 60 and above
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-[6px]">
                                                <input
                                                    type="checkbox"
                                                    id="woman-benefit"
                                                    className="h-4 w-4 cursor-pointer accent-brandAccent"
                                                    onChange={(e) =>
                                                        setisWomanBenefit(e.target.checked)
                                                    }
                                                />
                                                <label
                                                    htmlFor="woman-benefit"
                                                    className="cursor-pointer text-sm leading-[160%] tracking-[0.14px] text-neutral-500"
                                                >
                                                    Woman Benefit
                                                </label>
                                            </div>
                                        </div>

                                        <div className="mb-3 mt-4 flex h-8 items-center justify-between">
                                            <div className="flex items-center text-sm leading-[160%] tracking-[0.14px] text-neutral-500">
                                                Tenure in months &nbsp;
                                                <Select
                                                    onValueChange={(value) =>
                                                        setTenure(Number(value))
                                                    }
                                                >
                                                    <SelectTrigger className="w-auto appearance-none border-none bg-transparent p-0 focus:outline-none focus:ring-0 focus:ring-offset-0"></SelectTrigger>
                                                    <SelectContent className="w-[40px]">
                                                        <SelectGroup>
                                                            {fdData?.returnsAndTenure.map(
                                                                (item, index) => {
                                                                    return (
                                                                        <SelectItem
                                                                            key={index}
                                                                            value={String(
                                                                                item?.tenure,
                                                                            )}
                                                                        >
                                                                            {item.tenure} M
                                                                        </SelectItem>
                                                                    );
                                                                },
                                                            )}
                                                        </SelectGroup>
                                                    </SelectContent>
                                                </Select>
                                            </div>

                                            <p className="text-md font-medium leading-[160%] tracking-[0.14px]">
                                                {tenure} Months
                                            </p>
                                        </div>

                                        <div className="border-t-2 border-dashed border-neutral-50 py-s9">
                                            <div className="flex h-8 justify-between">
                                                <p className="text-sm leading-[160%] tracking-[0.14px] text-neutral-500">
                                                    Maturity amount
                                                </p>
                                                <p className="text-lg font-semibold leading-[164%] tracking-[-0.18px]">
                                                    {formatIndianCurrency(getMaturityAmount())}
                                                </p>
                                            </div>

                                            {(amtInvested ?? 0) < fdData?.minAmt && (
                                                <p className="text-center text-sm text-red-500">
                                                    Minimum invesment should be{" "}
                                                    {formatIndianCurrency(fdData?.minAmt)}
                                                </p>
                                            )}
                                            <RegularButton
                                                noIcon={true}
                                                label="Book Now"
                                                showRightIcon={false}
                                                disabled={
                                                    (amtInvested ?? 0) >= fdData?.minAmt
                                                        ? false
                                                        : true
                                                }
                                                variant={ButtonVariant.PrimaryDefault}
                                                size={ButtonSize.Medium}
                                                className="mt-s6 min-w-full max-w-full"
                                                onClick={() => setisModalOpen(true)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </ICMWrapper>
                </>
            )}
            <Dialog open={isModalOpen} onOpenChange={onClosePopup}>
                <DialogContent className="w-[90%] rounded-2xl p-5 md:w-full md:p-9">
                    <VisuallyHidden.Root>
                        <DialogTitle>Success Modal</DialogTitle>
                    </VisuallyHidden.Root>
                    <div className="md:w-full">
                        <h1 className="text-center text-xl font-semibold md:text-4xl">
                            Get the InCred Money app
                        </h1>
                        <div className="flex flex-col items-center justify-between pt-4 md:flex-row md:pt-8">
                            <div className="hidden md:block">
                                <p className="mb-4 text-md text-neutral-300 md:mb-6 md:text-lg">
                                    Scan this QR code
                                </p>
                                <div className="max-h-[166px] max-w-[166px] rounded-md border-2 border-neutral-200 p-2">
                                    <Image
                                        className="h-full w-full"
                                        src={`https://assets.incredmoney.com/images/svg/QR-code.svg`}
                                        alt="OR Code"
                                        width={20}
                                        height={20}
                                    />
                                </div>
                            </div>
                            {!isMobile && (
                                <div className="h-full md:border-l-2 md:border-dashed md:border-neutral-300 md:pl-5">
                                    <p className="text-md text-neutral-300 md:text-lg">
                                        Or, download the mobile app
                                    </p>
                                    <div className="mt-5 flex flex-col gap-4 md:mt-10">
                                        <RegularButton
                                            variant={ButtonVariant.SecondaryDefault}
                                            size={isMobile ? ButtonSize.Medium : ButtonSize.Large}
                                            label="iOS"
                                            showRightIcon={false}
                                            className="min-w-full max-w-max"
                                            onClick={() =>
                                                window.open(process.env.ICM_IOS_APP_URL, "_blank")
                                            }
                                        />
                                        <RegularButton
                                            variant={ButtonVariant.SecondaryDefault}
                                            size={isMobile ? ButtonSize.Medium : ButtonSize.Large}
                                            label="Android"
                                            showRightIcon={false}
                                            icon={<PlayStore />}
                                            className="min-w-full max-w-max"
                                            onClick={() =>
                                                window.open(process.env.ICM_PLAYSTORE_URL, "_blank")
                                            }
                                        />
                                    </div>
                                </div>
                            )}
                            {isMobile && (
                                <div className="mt-5 space-y-4">
                                    <RegularButton
                                        variant={ButtonVariant.SecondaryDefault}
                                        size={isMobile ? ButtonSize.Medium : ButtonSize.Large}
                                        label="iOS"
                                        showRightIcon={false}
                                        className="min-w-full max-w-max"
                                        onClick={() =>
                                            window.open(process.env.ICM_IOS_APP_URL, "_blank")
                                        }
                                    />
                                    <RegularButton
                                        variant={ButtonVariant.SecondaryDefault}
                                        size={isMobile ? ButtonSize.Medium : ButtonSize.Large}
                                        label="Android"
                                        showRightIcon={false}
                                        icon={<PlayStore />}
                                        className="min-w-full max-w-max"
                                        onClick={() =>
                                            window.open(process.env.ICM_PLAYSTORE_URL, "_blank")
                                        }
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <ScrollableBtn
                amtInvested={amtInvested ?? 0}
                setamtInvested={setamtInvested}
                setIsEditing={setIsEditing}
                isEditing={isEditing}
                fdData={fdData}
                setTenure={setTenure}
                tenure={tenure ?? 12}
                getInterest={getInterest}
                getMaturityAmount={getMaturityAmount}
                setIsModalOpen={setisModalOpen}
                isSeniorCitizen={isSeniorCitizen}
                setisSeniorCitizen={setisSeniorCitizen}
                isWomanBenefit={isWomanBenefit}
                setisWomanBenefit={setisWomanBenefit}
            />
        </>
    );
};

export default Page;
